import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState, type FormEvent } from "react";
import {
  Box,
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  Icon,
  Input,
  ScrollArea,
  Switch,
} from "@salestrak/ui";

import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { SettingsGroup, SettingsGroupContent, SettingsGroupRow } from "#app/components/settings/settings-group";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { PageHeader, PageHeaderBack, PageHeaderTitle } from "#app/components/page-header";
import { withPermission } from "#app/components/permissions/require-permission";

function CreateTeamPage() {
  const { replace } = useAppNavigate();
  const { createTeam } = useTeamMutations();

  const [teamName, setTeamName] = useState("");
  const [teamKey, setTeamKey] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-generate key from name (first 6 chars, uppercase)
  const handleNameChange = useCallback(
    (name: string) => {
      setTeamName(name);
      const autoKey = name
        .trim()
        .replace(/[^a-zA-Z0-9]/g, "")
        .slice(0, 6)
        .toUpperCase();
      setTeamKey(autoKey);
    },
    []
  );

  const handleCreate = useCallback(async () => {
    if (!teamName.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await createTeam({
        teamName: teamName.trim(),
        teamKey: teamKey.trim() || undefined,
        icon: { icon: ENTITY_KIND_ICON.team, color: "dark-grey" },
        private: isPrivate,
      });

      if (result && "teamId" in result) {
        replace(`/settings/teams/${result.teamId}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [teamName, teamKey, isPrivate, isSubmitting, createTeam, replace]);

  const isValid = teamName.trim().length >= 3;

  // A real form, so Enter in either field submits.
  const handleFormSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      void handleCreate();
    },
    [handleCreate],
  );

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="Teams" onClick={() => replace("/settings/teams")} />
          <PageHeaderTitle>Create team</PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box
            as="form"
            gap="xl"
            fill="width"
            maxWidth={800}
            onSubmit={handleFormSubmit}
          >
            <SettingsGroup label="General">
              <SettingsGroupContent>
                <Box gap="md">
                  <Field>
                    <FieldLabel>Team name</FieldLabel>
                    <Input
                      value={teamName}
                      onChange={(event) => handleNameChange(event.target.value)}
                      placeholder="e.g. Engineering"
                    />
                  </Field>
                  <Field>
                    <FieldLabel>Identifier</FieldLabel>
                    <Input
                      value={teamKey}
                      onChange={(event) => setTeamKey(event.target.value)}
                      placeholder="ENG"
                    />
                    <FieldDescription>
                      A short, unique key for this team (auto-generated from name).
                    </FieldDescription>
                  </Field>
                </Box>
              </SettingsGroupContent>
            </SettingsGroup>

            <SettingsGroup label="Privacy">
              <SettingsGroupRow
                label="Private team"
                subtitle="Only team members can see private teams and their projects."
              >
                <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
              </SettingsGroupRow>
            </SettingsGroup>

            <Button
              type="submit"
              className="w-full"
              disabled={!isValid || isSubmitting}
            >
              <Icon name="plus" />
              {isSubmitting ? "Creating..." : "Create team"}
            </Button>
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/teams/create")({
  component: withPermission("manage.teams", CreateTeamPage),
});
